import { Button } from "@/components/primitives/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/primitives/form";
import { Input } from "@/components/primitives/input";
import { FormStepProps } from "@/components/shared/Wizard/Wizard";
import { MAX_PASSWORD_LENGTH } from "@/config/constants";
import { isPasswordValid } from "@/middleware/recoveryKey";
import { createPasswordSchema, PasswordType } from "@/schemas/recoveryKey";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export default function PasswordStep({
  onNext,
  onPrevious,
  data,
  updateData,
}: FormStepProps<PasswordType>) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<PasswordType>({
    resolver: zodResolver(createPasswordSchema(t, isPasswordValid)),
    defaultValues: {
      password: data?.password || "",
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  function onSubmit(passwordData: PasswordType) {
    updateData(passwordData);
    onNext();
  }

  return (
    <div className="flex size-full select-none flex-col items-center justify-center gap-4">
      <h1 className="flex items-center self-start text-lg">
        <ArrowLeft className="mr-2 cursor-pointer" onClick={onPrevious} />
        {t("recover.passwordTitle")}
      </h1>
      <p className="w-full font-light opacity-50">
        {t("recover.passwordDescription")}
      </p>

      <Form {...form}>
        <form
          className="mt-4 flex w-full flex-col items-stretch gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("recover.password")}</FormLabel>
                <FormControl>
                  <div className="relative w-full">
                    <Input
                      className="rounded-lg border-border bg-card py-6 pr-9 placeholder:opacity-70"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("recover.password")}
                      autoComplete="password"
                      autoFocus
                      maxLength={MAX_PASSWORD_LENGTH}
                      {...field}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <EyeIcon className="size-5 text-foreground/50" />
                      ) : (
                        <EyeOffIcon className="size-5 text-foreground/50" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="mt-10 w-full"
            disabled={form.formState.isSubmitting}
          >
            {t("next")}
          </Button>
        </form>
      </Form>
    </div>
  );
}
